import { csvField } from '@/features/monopoly/winnersCsv';

export { downloadCsv } from '@/features/monopoly/winnersCsv';
import type { SurveyStrings } from './i18n';
import type { Figures, Season } from './seasons';
import { ratio } from './utils';

/*
 * CSV exports of the Système U pages, built in the browser from what the page
 * already shows — no backend involved.
 *
 * Semicolon-separated with a UTF-8 BOM: the client opens these in Excel on a
 * French machine, which splits on ";" and needs the BOM to read "Système" and
 * "Hyper U · 18 boulevard de l'Europe" correctly. Headers and status words are
 * in the page's language. Numbers are raw (no thousands separator) so the sheet
 * can add them up, with the language's decimal mark — a French Excel reads
 * "2.08" as text, "2,08" as a number. Fields go through the same
 * formula-injection guard as the Monopoly export.
 */

const SEP = ';';
const BOM = '\uFEFF';

export function toCsv(header: string[], rows: Array<Array<string | number | null>>): string {
  const line = (cells: Array<string | number | null>) => cells.map((c) => csvField(c == null ? '' : String(c))).join(SEP);
  return BOM + [line(header), ...rows.map(line)].join('\r\n') + '\r\n';
}

const round = (n: number | null, digits = 0) => (n == null ? null : Number(n.toFixed(digits)));
const hours = (sec: number | null) => (sec == null ? null : Math.round(sec / 3600));
/** started − completed; null when either is unmeasured, never below 0. */
const open = (started: number | null, completed: number | null) => (started == null || completed == null ? null : Math.max(0, started - completed));
/** 2.0833 → "2,08" (fr) / "2.08" (en). */
const decimal = (n: number | null, t: SurveyStrings) => (n == null ? null : String(round(n, 2)).replace('.', t.csvDecimal));

type Row = Pick<Figures, 'uniquePlayers' | 'playersStarted' | 'quizzesStarted' | 'plays' | 'ticketsIssued' | 'timeInAppSec'>;

/** The figures of one line: a week, a season or the campaign. */
const figureCells = (f: Row, t: SurveyStrings) => [
  f.uniquePlayers,
  f.playersStarted,
  f.quizzesStarted,
  f.plays,
  open(f.quizzesStarted, f.plays),
  decimal(ratio(f.plays, f.uniquePlayers), t),
  f.ticketsIssued,
  hours(f.timeInAppSec),
  round(ratio(f.timeInAppSec, f.uniquePlayers)),
];

/**
 * The weekly summary as the table shows it: each season's weeks (numbered
 * inside the season), the season's total, then the campaign total when both
 * seasons are exported.
 */
export function weeklySummaryCsv(seasons: Season[], figures: Figures, t: SurveyStrings): string {
  const h = t.csvWeekly;
  const header = [
    h.season,
    h.week,
    h.start,
    h.end,
    h.status,
    h.uniquePlayers,
    h.playersStarted,
    h.quizzesStarted,
    h.plays,
    h.notCompleted,
    h.playsPerUser,
    h.tickets,
    h.timeInAppHours,
    h.timePerUserSec,
  ];
  const rows: Array<Array<string | number | null>> = [];
  for (const s of seasons) {
    const season = t.season(s.n);
    s.weeks.forEach((w, k) => rows.push([season, k + 1, w.start, w.end, t.weekStatus[w.status], ...figureCells(w, t)]));
    rows.push([season, t.csvTotal, s.start, s.end, t.weekStatus[s.status], ...figureCells(s.figures, t)]);
  }
  if (seasons.length > 1) rows.push([t.campaignTotal, null, null, null, null, ...figureCells(figures, t)]);
  return toCsv(header, rows);
}
